import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, X, Trash2, Mail, MessageCircle, Paperclip, AlertCircle, CheckCircle2, FileEdit } from 'lucide-react';
import { DeliveryPill } from './email/DeliveryPill';
import { useAuth } from '../contexts/AuthContext';
import { toSentenceCase } from '../utils/textFormat';
import EmailComposeModal from './email/EmailComposeModal';
import EmailThreadModal from './email/EmailThreadModal';

import { API_BASE as API } from '../config';

const getTimeAgo = (dateStr, byName) => {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  let text;
  if (mins < 1) text = 'just now';
  else if (mins < 60) text = `${mins} minute${mins !== 1 ? 's' : ''} ago`;
  else {
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) text = `${hrs} hour${hrs !== 1 ? 's' : ''} ago`;
    else {
      const days = Math.floor(hrs / 24);
      if (days < 30) text = `${days} day${days !== 1 ? 's' : ''} ago`;
      else {
        const months = Math.floor(days / 30);
        if (months < 12) text = `${months} month${months !== 1 ? 's' : ''} ago`;
        else {
          const years = Math.floor(months / 12);
          text = `${years} year${years !== 1 ? 's' : ''} ago`;
        }
      }
    }
  }
  if (byName) text += ` by ${byName}`;
  return text;
};

const Avatar = ({ name, color }) => {
  const initials = (name || '??')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  return (
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
      style={{ backgroundColor: color || '#3b82f6' }}
      data-testid="note-avatar"
    >
      {initials}
    </div>
  );
};

const AVATAR_COLORS = ['#3b82f6', '#22c55e', '#ef4444', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6'];
const getColor = (name) => {
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getLossTypeTitle = (claim) => {
  const types = [];
  if (claim.al) types.push('Auto Liability');
  if (claim.pd) types.push('Physical Damage');
  if (claim.cd) types.push('Cargo Damage');
  if (claim.ocac) types.push('Occupational Accident');
  if (claim.wc) types.push('Workers Compensation');
  if (types.length === 0) return 'Claim Processing';
  return types.join(' / ') + ' Processing';
};

const LOSS_TYPE_LABELS = {
  al: 'Auto Liability Processing',
  pd: 'Physical Damage Processing',
  cd: 'Cargo Damage Processing',
  ocac: 'Occupational Accident Processing',
  wc: 'Workers Compensation Processing',
};

const LOSS_TYPE_NAMES = {
  al: 'Auto Liability',
  pd: 'Physical Damage',
  cd: 'Cargo Damage',
  ocac: 'Occupational Accident',
  wc: 'Workers Compensation',
};

const LOSS_TYPE_KEYS = ['al', 'pd', 'cd', 'ocac', 'wc'];

const getRelatedClaims = (claim, currentType) => {
  // Build active types from boolean flags first
  const activeTypes = new Set(LOSS_TYPE_KEYS.filter(k => claim[k]));
  // Fallback: parse report_no for legacy claims with missing flags
  // Handles both "AFT/ALPD-..." and "HEL/AL-PD-..." formats
  if (claim.report_no) {
    const seg = (claim.report_no.split('/')[1] || '').split(/[-]/);
    const codePart = seg.filter(s => /^[A-Z]+$/.test(s)).join('');
    const codeMap = { OCAC: 'ocac', WC: 'wc', AL: 'al', PD: 'pd', CD: 'cd' };
    let rem = codePart;
    for (const [code, key] of Object.entries(codeMap)) {
      if (rem.includes(code)) { activeTypes.add(key); rem = rem.replace(code, ''); }
    }
  }
  return [...activeTypes]
    .filter(k => k !== currentType)
    .map(k => ({
      key: k,
      type: LOSS_TYPE_NAMES[k],
      status: claim.type_data?.[k]?.status || claim[`${k}_status`] || claim.status || 'New',
    }));
};

const getTypeData = (claim, lossType) => {
  const td = claim.type_data?.[lossType] || {};
  const c = claim;
  const k = lossType;
  return {
    ...td,
    submittal: td.submittal || c[`${k}_submittal`] || c.submittal || 'Pending',
    status: td.status || c[`${k}_status`] || c.status || 'New',
    initial_amount: td.initial_amount || c[`${k}_initial_amount`] || c.initial_amount || '',
    final_amount: td.final_amount || c[`${k}_final_amount`] || c.final_amount || '',
    // Insurance fields — legacy uses _ins_company, _claims_no, _adjusters_ph_no, _adjusters_ph_ext
    insurance_company: td.insurance_company || c[`${k}_ins_company`] || c[`${k}_insurance_company`] || c.insurance_company || '',
    insurance_claim_no: td.insurance_claim_no || c[`${k}_claims_no`] || c[`${k}_insurance_claim_no`] || c.insurance_claim_no || '',
    adjuster: td.adjuster || c[`${k}_adjuster`] || c.adjuster || '',
    adjusters_phone: td.adjusters_phone || c[`${k}_adjusters_ph_no`] || c[`${k}_adjusters_phone`] || c.adjusters_phone || '',
    adjusters_extension: td.adjusters_extension || c[`${k}_adjusters_ph_ext`] || c[`${k}_adjusters_extension`] || c.adjusters_extension || '',
    adjusters_email: td.adjusters_email || c[`${k}_adjusters_email`] || c.adjusters_email || '',
    // In-House fields — legacy uses _in_house_claims_no, _contact_ph_no, _contact_ph_ext
    other_party: td.other_party || c[`${k}_other_party`] || c.other_party || '',
    in_house_claim_no: td.in_house_claim_no || c[`${k}_in_house_claims_no`] || c[`${k}_in_house_claim_no`] || c.in_house_claim_no || '',
    contact: td.contact || c[`${k}_contact`] || c.contact || '',
    contact_phone: td.contact_phone || c[`${k}_contact_ph_no`] || c[`${k}_contact_phone`] || c.contact_phone || '',
    extension: td.extension || c[`${k}_contact_ph_ext`] || c[`${k}_extension`] || c.extension || '',
    contact_email: td.contact_email || c[`${k}_contact_email`] || c.contact_email || '',
  };
};

const fmtPhone = (val) => {
  const d = String(val || '').replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

/** Mailto-style clickable email — clicking opens the compose modal with the
 *  address pre-filled instead of handing off to the OS mail app. Falls back
 *  to plain text when no value is set. */
const ClickableEmail = ({ value, onClick, testid }) => {
  if (!value) return null;
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); onClick?.(value); }}
      className="text-teal-600 hover:underline hover:text-teal-700 transition-colors"
      data-testid={testid}
    >
      {value}
    </button>
  );
};

const NotesModal = ({ open, onClose, claim, lossType, onFullInfo, onSwitchType, onNoteAdded, initialTab = 'comments' }) => {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [posting, setPosting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeLossType, setActiveLossType] = useState(lossType);
  const [activeTab, setActiveTab] = useState(initialTab); // 'comments' | 'emails' | 'drafts'

  // Reset to the requested initial tab every time the modal transitions open.
  // Without this, the toast-deep-link path would always land on whatever tab
  // was last selected instead of jumping straight to "Emails".
  useEffect(() => {
    if (open) setActiveTab(initialTab);
  }, [open, initialTab]);
  const [emails, setEmails] = useState([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [resumeDraft, setResumeDraft] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composePrefill, setComposePrefill] = useState({ to: '', subject: '' });
  const [threadEmailId, setThreadEmailId] = useState(null);
  const [threadToken, setThreadToken] = useState(null);
  const listRef = useRef(null);

  useEffect(() => { setActiveLossType(lossType); }, [lossType]);

  useEffect(() => {
    if (open && claim?.report_no && activeLossType) {
      setLoading(true);
      axios
        .get(`${API}/claim-notes`, { params: { report_no: claim.report_no, loss_type: activeLossType } })
        .then((res) => setNotes(res.data))
        .catch(() => setNotes([]))
        .finally(() => setLoading(false));
    }
    if (!open) {
      setNotes([]);
      setNewNote('');
    }
  }, [open, claim?.report_no, activeLossType]);

  useEffect(() => {
    // Load emails AS SOON AS the modal opens (not only when the user
    // switches to the Emails tab) so the "(n) Emails" badge in the tab
    // strip is accurate on first paint. Previously the count showed 0 until
    // the tab was clicked, which made it look like there were no emails
    // when there were actually 20.
    if (open && claim?.report_no) {
      setEmailsLoading(true);
      axios
        .get(`${API}/claim-emails`, {
          params: {
            report_no: claim.report_no,
            // Scope emails to the active loss type so AL/PD/CD/etc. each have
            // their own correspondence thread, matching how Comments work.
            // Legacy rows without loss_type are server-side included so they
            // don't silently disappear.
            ...(activeLossType ? { loss_type: activeLossType } : {}),
          },
        })
        .then((res) => setEmails(res.data || []))
        .catch(() => setEmails([]))
        .finally(() => setEmailsLoading(false));
    }
  }, [open, claim?.report_no, activeLossType]);

  /** Refresh the per-claim, per-user, per-loss-type drafts list. Kept as a
   *  named function so the compose modal can call it back after auto-save /
   *  send / discard. Drafts are sorted server-side by updated_at desc. */
  const refreshDrafts = React.useCallback(() => {
    if (!claim?.report_no) return;
    setDraftsLoading(true);
    axios
      .get(`${API}/email-drafts`, {
        params: {
          report_no: claim.report_no,
          ...(activeLossType ? { loss_type: activeLossType } : {}),
        },
      })
      .then((res) => setDrafts(res.data || []))
      .catch(() => setDrafts([]))
      .finally(() => setDraftsLoading(false));
  }, [claim?.report_no, activeLossType]);

  useEffect(() => {
    if (open && claim?.report_no) refreshDrafts();
    if (!open) setDrafts([]);
  }, [open, refreshDrafts]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [notes]);

  const openCompose = (toEmail = '') => {
    setComposePrefill({
      to: toEmail || '',
      subject: claim?.report_no ? `Re: Claim ${claim.report_no}` : '',
    });
    setResumeDraft(null);
    setComposeOpen(true);
  };

  const openDraft = (draft) => {
    // Resume a saved draft. The compose modal will hydrate from
    // `initialDraft` rather than the prefill defaults.
    setComposePrefill({ to: '', subject: '' });
    setResumeDraft(draft);
    setComposeOpen(true);
  };

  const discardDraft = async (draftId, e) => {
    e?.stopPropagation();
    if (!window.confirm('Discard this draft?')) return;
    try {
      await axios.delete(`${API}/email-drafts/${draftId}`);
      setDrafts((prev) => prev.filter((d) => d.draft_id !== draftId));
      toast.success('Draft discarded');
    } catch {
      toast.error('Failed to discard draft');
    }
  };

  const handleEmailSent = (newEmail) => {
    // Splice the freshly-sent email into the list so users see it without a
    // re-fetch. The backend response mirrors the list shape.
    setEmails((prev) => [newEmail, ...prev]);
    setActiveTab('emails');
  };

  /** Group flat email rows by thread_token (falling back to email_id when a
   *  message has no thread context yet, e.g. a one-off send that hasn't been
   *  replied to). Returns one object per thread: latest message first, with
   *  count + last_at + a `messages[]` array containing every member sorted
   *  oldest → newest. */
  const groupedThreads = React.useMemo(() => {
    const buckets = new Map();
    for (const em of emails) {
      // For a sent email, its own reply_token IS the thread it kicks off.
      // For a received email, thread_token mirrors the original outbound's
      // reply_token (set server-side in the inbound webhook). Fall back to
      // email_id so each ungrouped message still renders as a thread-of-one.
      const key = em.thread_token || em.reply_token || em.email_id;
      if (!buckets.has(key)) {
        buckets.set(key, { thread_token: key, messages: [], last_at: '', received_count: 0 });
      }
      const b = buckets.get(key);
      b.messages.push(em);
      if (em.direction === 'received') b.received_count += 1;
      const t = em.sent_at || em.created_at || '';
      if (t > b.last_at) b.last_at = t;
    }
    const out = Array.from(buckets.values());
    // Latest thread first, by last_at desc
    out.sort((a, b) => (b.last_at || '').localeCompare(a.last_at || ''));
    // Inside each thread, sort messages oldest-first so the head is the
    // original send and the tail is the newest reply (used for preview).
    out.forEach((t) => t.messages.sort((a, b) => (a.sent_at || a.created_at || '').localeCompare(b.sent_at || b.created_at || '')));
    return out;
  }, [emails]);

  const handlePost = async () => {
    if (!newNote.trim()) return;
    setPosting(true);
    try {
      const res = await axios.post(`${API}/claim-notes`, {
        report_no: claim.report_no,
        text: newNote.trim(),
        loss_type: activeLossType,
      });
      setNotes((prev) => [...prev, res.data]);
      setNewNote('');
      onNoteAdded?.();
    } catch {
      toast.error('Failed to post note');
    } finally {
      setPosting(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/claim-notes/${noteId}`);
      setNotes((prev) => prev.filter((n) => n.note_id !== noteId));
      toast.success('Note deleted');
      onNoteAdded?.();
    } catch {
      toast.error('Failed to delete note');
    }
  };

  const canDeleteNotes = user?.role === 'admin' || user?.role === 'super_admin';

  if (!open || !claim) return null;

  const td = getTypeData(claim, activeLossType);
  const relatedClaims = getRelatedClaims(claim, activeLossType);
  const title = LOSS_TYPE_LABELS[activeLossType] || getLossTypeTitle(claim);
  const updatedAgo = (td.updated_at || claim.updated_at) ? getTimeAgo(td.updated_at || claim.updated_at, claim.updated_by_name) : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-2 md:p-0" data-testid="notes-modal-overlay">
      <div
        className="bg-gray-50 rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] md:max-h-[85vh] flex flex-col"
        data-testid="notes-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b bg-white rounded-t-lg">
          <h2 className="text-base md:text-xl font-bold text-blue-600 italic truncate" data-testid="notes-modal-title">
            {title}
          </h2>
          <div className="flex items-center gap-3 md:gap-4 shrink-0">
            <button
              className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-blue-600 transition-colors"
              data-testid="notes-modal-fullinfo"
              onClick={() => { onFullInfo?.(); }}
            >
              <FileText className="w-4 h-4" />
              Full Info
            </button>
            <button
              onClick={onClose}
              className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-red-500 transition-colors"
              data-testid="notes-modal-exit"
            >
              <X className="w-4 h-4" />
              Exit
            </button>
          </div>
        </div>

        {/* Body — stacks vertically on mobile, two columns on desktop */}
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* Left column — Claim Info */}
          <div className="md:w-[400px] flex-shrink-0 border-b md:border-b-0 md:border-r bg-white p-4 md:p-6 overflow-y-auto max-h-[35vh] md:max-h-none" data-testid="notes-modal-info">
            <h3 className="text-lg font-bold text-gray-900 mb-1">{claim.company_name}</h3>
            <p className="text-sm text-gray-700 mb-0.5">{claim.report_no}</p>
            {claim.dot_recordable && <p className="text-red-600 font-bold text-sm mb-0.5" data-testid="notes-dot-recordable">DOT Recordable</p>}
            {updatedAgo && (
              <p className="text-xs text-blue-500 italic mb-4">
                Record last updated {updatedAgo}.
              </p>
            )}

            <hr className="border-gray-200 my-3" />

            <div className="flex gap-6 text-sm">
              <div>
                <span className="font-semibold text-gray-800">Status: </span>
                <span className="text-gray-600">{td.status || 'New'}</span>
              </div>
              <div>
                <span className="font-semibold text-gray-800">Submittal: </span>
                <span className="text-gray-600">{td.submittal || 'Pending'}</span>
              </div>
            </div>

            <hr className="border-gray-200 my-3" />

            <div className="flex gap-6 text-sm">
              <span>{td.initial_amount ? <><span className="font-semibold text-gray-800">Initial Amount: </span><span className="text-gray-600">${Number(td.initial_amount).toLocaleString()}</span></> : <span className="italic text-gray-400">No Initial Amount</span>}</span>
              <span>{td.final_amount ? <><span className="font-semibold text-gray-800">Final Amount: </span><span className="text-gray-600">${Number(td.final_amount).toLocaleString()}</span></> : <span className="italic text-gray-400">No Final Amount</span>}</span>
            </div>

            <hr className="border-gray-200 my-3" />

            {/* Submittal-specific info for current type */}
            {td.submittal === 'In-House' && (
              <div className="space-y-1.5 text-sm text-gray-700">
                <p><span className="font-semibold">Other Party: </span>{td.other_party || ''}</p>
                <p><span className="font-semibold">In-House Claim #: </span>{td.in_house_claim_no || ''}</p>
                <p><span className="font-semibold">Contact: </span>{td.contact || ''}</p>
                <p><span className="font-semibold">Phone #: </span>{fmtPhone(td.contact_phone)}{td.extension ? ` ext. ${td.extension}` : ''}</p>
                <p><span className="font-semibold">Email: </span><ClickableEmail value={td.contact_email} onClick={openCompose} testid="email-contact-link" /></p>
              </div>
            )}
            {td.submittal === 'Insurance' && (
              <div className="space-y-1.5 text-sm text-gray-700">
                <p><span className="font-semibold">Insurance Company: </span>{td.insurance_company || ''}</p>
                <p><span className="font-semibold">Insurance Claim #: </span>{td.insurance_claim_no || ''}</p>
                <p><span className="font-semibold">Adjuster: </span>{td.adjuster || ''}</p>
                <p><span className="font-semibold">Phone #: </span>{fmtPhone(td.adjusters_phone)}{td.adjusters_extension ? ` ext. ${td.adjusters_extension}` : ''}</p>
                <p><span className="font-semibold">Email: </span><ClickableEmail value={td.adjusters_email} onClick={openCompose} testid="email-adjuster-link" /></p>
              </div>
            )}
            {(td.submittal !== 'In-House' && td.submittal !== 'Insurance') && (
              <p className="text-sm italic text-gray-400">No In-House or Insurance details</p>
            )}

            {relatedClaims.length > 0 && (
              <>
                <hr className="border-gray-200 my-3" />
                <p className="text-sm font-semibold italic text-gray-800 mb-2">Related Claim(s):</p>
                <div className="space-y-2">
                  {relatedClaims.map((rc) => (
                    <button key={rc.key}
                      onClick={() => { setActiveLossType(rc.key); onSwitchType?.(rc.key); }}
                      className="grid grid-cols-[1fr_140px] gap-2 text-sm w-full text-left hover:bg-blue-50 rounded px-2 py-1 transition-colors group"
                      data-testid={`related-claim-${rc.key}`}>
                      <span className="font-semibold text-blue-600 group-hover:underline">{rc.type}</span>
                      <span className="text-gray-500 text-left whitespace-nowrap">Status: {rc.status}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Right column — Comments / Emails.
              `min-w-0` is critical here: without it, the flex item refuses to
              shrink below its largest child's intrinsic width — so a long
              unbroken body_preview line (a typical email signature with no
              spaces around pipe characters) blew past the modal edge instead
              of being truncated. */}
          <div className="flex-1 flex flex-col bg-gray-50 p-4 md:p-6 min-h-0 min-w-0" data-testid="notes-modal-comments">
            {/* Tabs */}
            <div className="flex items-center gap-1 mb-4 border-b border-gray-200">
              <button
                onClick={() => setActiveTab('comments')}
                className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px inline-flex items-center gap-1.5 ${
                  activeTab === 'comments' ? 'border-teal-500 text-teal-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                data-testid="tab-comments"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                {notes.length} Comment{notes.length !== 1 ? 's' : ''}
              </button>
              <button
                onClick={() => setActiveTab('emails')}
                className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px inline-flex items-center gap-1.5 ${
                  activeTab === 'emails' ? 'border-teal-500 text-teal-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                data-testid="tab-emails"
              >
                <Mail className="w-3.5 h-3.5" />
                {emails.length} Email{emails.length !== 1 ? 's' : ''}
              </button>
              {(drafts.length > 0 || activeTab === 'drafts') && (
                <button
                  onClick={() => setActiveTab('drafts')}
                  className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px inline-flex items-center gap-1.5 ${
                    activeTab === 'drafts' ? 'border-amber-500 text-amber-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="tab-drafts"
                >
                  <FileEdit className="w-3.5 h-3.5" />
                  {drafts.length} Draft{drafts.length !== 1 ? 's' : ''}
                </button>
              )}
              {(activeTab === 'emails' || activeTab === 'drafts') && (
                <button
                  onClick={() => openCompose()}
                  className="ml-auto px-3 py-1 text-xs font-medium bg-teal-600 hover:bg-teal-700 text-white rounded transition-colors"
                  data-testid="new-email-btn"
                >
                  + New Email
                </button>
              )}
            </div>

            {activeTab === 'comments' ? (
              <>
                {/* Comments list */}
                <div className="flex-1 overflow-y-auto space-y-4 mb-4" ref={listRef} data-testid="notes-list">
                  {loading ? (
                    <p className="text-sm text-gray-400 text-center py-8">Loading...</p>
                  ) : notes.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-8" data-testid="notes-empty">No comments yet.</p>
                  ) : (
                    notes.map((note) => (
                      <div
                        key={note.note_id}
                        className="bg-white rounded-lg border border-gray-200 p-4 group/note"
                        data-testid={`note-card-${note.note_id}`}
                      >
                        <div className="flex items-start gap-3">
                          <Avatar name={note.created_by_name} color={getColor(note.created_by_name)} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2 mb-1">
                              <span className="text-sm font-bold text-gray-800">{note.created_by_name}</span>
                              <span className="text-xs text-gray-400">{new Date(note.created_at).toLocaleString()}</span>
                              {canDeleteNotes && (
                                <button
                                  onClick={() => handleDeleteNote(note.note_id)}
                                  className="ml-auto opacity-0 group-hover/note:opacity-100 transition-opacity text-gray-400 hover:text-red-500 p-0.5"
                                  title="Delete note"
                                  data-testid={`delete-note-${note.note_id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{note.text}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Add comment */}
                <div className="flex items-start gap-3" data-testid="notes-add-section">
                  <Avatar name={user?.name} color={getColor(user?.name)} />
                  <div className="flex-1 flex flex-col gap-2">
                    <textarea
                      placeholder="Add a comment..."
                      value={newNote}
                      onChange={(e) => setNewNote(toSentenceCase(e.target.value))}
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg p-3 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                      data-testid="notes-comment-input"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handlePost();
                      }}
                    />
                    <div className="flex justify-end">
                      <button
                        onClick={handlePost}
                        disabled={posting || !newNote.trim()}
                        className="px-5 py-1.5 bg-gray-200 text-sm font-medium text-gray-700 rounded hover:bg-gray-300 transition-colors disabled:opacity-50"
                        data-testid="notes-post-btn"
                      >
                        {posting ? 'Posting...' : 'Post'}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : activeTab === 'emails' ? (
              /* Emails list */
              <div className="flex-1 overflow-y-auto space-y-2.5" data-testid="emails-list">
                {emailsLoading ? (
                  <p className="text-sm text-gray-400 text-center py-8">Loading emails…</p>
                ) : emails.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8" data-testid="emails-empty">
                    No emails sent for this claim yet. Click an email address on the left or “+ New Email” to start.
                  </p>
                ) : (
                  groupedThreads.map((thread) => {
                    // Most recent message drives the row preview / colour.
                    const head = thread.messages[thread.messages.length - 1];
                    const isInbound = head.direction === 'received';
                    const count = thread.messages.length;
                    return (
                      <button
                        key={thread.thread_token}
                        onClick={() => {
                          // Single-message threads still open the modal but
                          // via the email_id (cheaper, no thread fetch).
                          if (count <= 1) {
                            setThreadEmailId(head.email_id);
                          } else {
                            setThreadToken(thread.thread_token);
                          }
                        }}
                        className={`w-full text-left rounded-lg border p-3 hover:shadow-sm transition-all ${
                          isInbound
                            ? 'bg-blue-50/40 border-blue-200 hover:border-blue-300'
                            : 'bg-white border-gray-200 hover:border-teal-300'
                        }`}
                        data-testid={`email-card-${head.email_id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`relative w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isInbound ? 'bg-blue-100' : 'bg-teal-100'
                          }`}>
                            {head.status === 'failed' ? (
                              <AlertCircle className="w-4 h-4 text-red-500" />
                            ) : isInbound ? (
                              <Mail className="w-4 h-4 text-blue-600" style={{ transform: 'scaleX(-1)' }} />
                            ) : head.status === 'sent' ? (
                              <CheckCircle2 className="w-4 h-4 text-teal-600" />
                            ) : (
                              <Mail className="w-4 h-4 text-teal-600" />
                            )}
                            {count > 1 && (
                              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-gray-700 text-white text-[10px] font-semibold rounded-full" data-testid={`thread-count-${thread.thread_token}`}>
                                {count}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline justify-between gap-2 mb-0.5">
                              <span className="text-sm font-semibold text-gray-800 truncate">
                                {isInbound && <span className="text-[10px] uppercase tracking-wide bg-blue-200 text-blue-800 px-1.5 py-0.5 rounded mr-1.5">Reply</span>}
                                {count > 1 && <span className="text-[10px] uppercase tracking-wide bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded mr-1.5">{count} msgs</span>}
                                {head.subject || '(no subject)'}
                              </span>
                              <span className="text-[11px] text-gray-400 flex-shrink-0">
                                {thread.last_at ? new Date(thread.last_at).toLocaleString() : ''}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 truncate mb-1">
                              {isInbound ? `From: ${head.from}` : `To: ${(head.to || []).join(', ')}${head.cc?.length ? ` · Cc: ${head.cc.join(', ')}` : ''}`}
                            </p>
                            {head.body_preview && (
                              <p className="text-xs text-gray-500 truncate">{head.body_preview}</p>
                            )}
                            <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-400">
                              <span>By {head.created_by_name}</span>
                              {head.attachments?.length > 0 && (
                                <span className="inline-flex items-center gap-1">
                                  <Paperclip className="w-3 h-3" />
                                  {head.attachments.length}
                                </span>
                              )}
                              {head.status === 'failed' && <span className="text-red-500 font-medium">Failed</span>}
                              {/* Delivery / engagement pill — only renders for outbound rows. */}
                              <DeliveryPill msg={head} />
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            ) : (
              /* Drafts list — only this user's saved drafts on this claim/loss type */
              <div className="flex-1 overflow-y-auto space-y-2.5" data-testid="drafts-list">
                {draftsLoading ? (
                  <p className="text-sm text-gray-400 text-center py-8">Loading drafts…</p>
                ) : drafts.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8" data-testid="drafts-empty">
                    No drafts. Start a new email and your work is saved automatically.
                  </p>
                ) : (
                  drafts.map((d) => {
                    const preview = (d.body_text || '').trim().slice(0, 120);
                    const toLine = (d.to || []).join(', ');
                    return (
                      <div
                        key={d.draft_id}
                        role="button"
                        tabIndex={0}
                        onClick={() => openDraft(d)}
                        onKeyDown={(e) => {
                          // Keyboard parity: Enter / Space activates the card,
                          // matching native <button> semantics now that the
                          // outer element is a <div> (we changed away from a
                          // <button> because a nested discard <button> inside
                          // a parent <button> is invalid HTML).
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openDraft(d);
                          }
                        }}
                        className="w-full text-left rounded-lg border border-amber-200 bg-amber-50/40 hover:border-amber-300 hover:shadow-sm transition-all p-3 group/draft cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-300"
                        data-testid={`draft-card-${d.draft_id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-full flex items-center justify-center bg-amber-100 flex-shrink-0">
                            <FileEdit className="w-4 h-4 text-amber-700" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline justify-between gap-2 mb-0.5">
                              <span className="text-sm font-semibold text-gray-800 truncate">
                                <span className="text-[10px] uppercase tracking-wide bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded mr-1.5">Draft</span>
                                {d.subject || '(no subject)'}
                              </span>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="text-[11px] text-gray-400">
                                  {d.updated_at ? new Date(d.updated_at).toLocaleString() : ''}
                                </span>
                                <button
                                  onClick={(e) => discardDraft(d.draft_id, e)}
                                  className="opacity-0 group-hover/draft:opacity-100 transition-opacity text-gray-400 hover:text-red-500 p-0.5"
                                  title="Discard draft"
                                  data-testid={`discard-draft-${d.draft_id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 truncate mb-1">
                              To: {toLine || <span className="italic">no recipient yet</span>}
                            </p>
                            {preview && (
                              <p className="text-xs text-gray-500 truncate">{preview}</p>
                            )}
                            {d.attachments?.length > 0 && (
                              <div className="flex items-center gap-1 mt-1.5 text-[11px] text-gray-400">
                                <Paperclip className="w-3 h-3" />
                                {d.attachments.length} attachment{d.attachments.length !== 1 ? 's' : ''}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Compose modal */}
      <EmailComposeModal
        open={composeOpen}
        onClose={() => { setComposeOpen(false); setResumeDraft(null); refreshDrafts(); }}
        reportNo={claim?.report_no}
        lossType={activeLossType}
        initialTo={composePrefill.to}
        initialSubject={composePrefill.subject}
        initialDraft={resumeDraft}
        onSent={handleEmailSent}
        onDraftChange={refreshDrafts}
      />
      {/* Thread viewer */}
      <EmailThreadModal
        open={!!threadEmailId || !!threadToken}
        onClose={() => { setThreadEmailId(null); setThreadToken(null); }}
        emailId={threadEmailId}
        threadToken={threadToken}
        /* Pass the claim through so the AttachmentChip can offer
         * "Save in claim" → open DocsModal with the attachment pre-loaded. */
        claim={claim}
        onReply={({ to, subject }) => {
          // Reply button on the thread modal pre-fills the compose modal so
          // users don't have to retype the recipient or subject.
          setComposePrefill({ to: to || '', subject: subject || '' });
          setComposeOpen(true);
        }}
      />
    </div>
  );
};

export default NotesModal;
